import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { Shield, Lock, Eye, Users, FileText, Database, Globe, AlertTriangle, Mail, ArrowLeft, Clock } from "lucide-react"
import { useTranslation } from "@/lib/i18n"

import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'

export const Route = createFileRoute('/privacy')({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: 'Privacy Policy - Zapify',
        description: 'Your privacy is important to us. Learn how we collect, use, and protect your personal information.',
        url: 'https://zapify.pro/privacy',
        publishedTime: '2024-12-28T00:00:00Z',
      }),
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Privacy Policy - Zapify" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/privacy" }
    ],
    scripts: [
      createWebPageSchema({
        name: 'Privacy Policy - Zapify',
        description: 'Your privacy is important to us. Learn how we collect, use, and protect your personal information.',
        url: 'https://zapify.pro/privacy',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          {
            position: 1,
            name: 'Home',
            item: 'https://zapify.pro',
          },
          {
            position: 2,
            name: 'Privacy Policy',
            item: 'https://zapify.pro/privacy',
          },
        ],
      }),
    ],
  }),
  component: PrivacyPage,
})

export function PrivacyPage() {
  const { translations: t } = useTranslation();
  return (
    <main className="min-h-screen bg-white dark:bg-black py-20 px-4 md:px-8 transition-colors">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <Badge variant="outline" className="px-4 py-1 border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 mb-2">
            {t.legal.lastUpdated}: December 28, 2024
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
            {t.legal.privacyPolicy}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            Your privacy is important to us. This policy outlines how we collect,
            use, and protect your personal information.
          </p>
        </div>

        <Card className="border-gray-200 dark:border-neutral-800 shadow-lg bg-white dark:bg-neutral-900">
          <CardContent className="p-8 md:p-12 space-y-12">
            <div className="prose prose-gray dark:prose-invert max-w-none space-y-12">
              
              {/* Section 1 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    1. What Information Do We Collect?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We collect personal information that you
                    provide to us.
                  </p>
                  <p>
                    We collect personal information that you voluntarily provide
                    to us when you register on the Services, express an interest
                    in obtaining information about us or our products and
                    Services, when you participate in activities on the
                    Services, or otherwise when you contact us.
                  </p>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Personal Information Provided by You.
                    </h3>
                    <p>
                      The personal information that we collect depends on the
                      context of your interactions with us and the Services, the
                      choices you make, and the products and features you use.
                      The personal information we collect may include the
                      following: names, phone numbers, email addresses, job
                      titles, usernames, passwords, contact preferences, contact
                      or authentication data, billing addresses, debit/credit
                      card numbers.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Sensitive Information.
                    </h3>
                    <p>We do not process sensitive information.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Payment Data.
                    </h3>
                    <p>
                      We may collect data necessary to process your payment if
                      you make purchases, such as your payment instrument number
                      (such as a credit card number), and the security code
                      associated with your payment instrument. All payment data
                      is stored by Stripe.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Social Media Login Data.
                    </h3>
                    <p>
                      We may provide you with the option to register with us
                      using your existing social media account details, like
                      your Facebook, Twitter, or other social media account. If
                      you choose to register in this way, we will collect the
                      information described in the section called "HOW DO WE
                      HANDLE YOUR SOCIAL LOGINS?" below.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Information automatically collected
                    </h3>
                    <p>
                      <strong>In Short:</strong> Some information — such as your Internet
                      Protocol (IP) address and/or browser and device
                      characteristics — is collected automatically when you
                      visit our Services.
                    </p>
                     <p>
                      We automatically collect certain
                      information when you visit, use, or navigate the Services.
                      This information does not reveal your specific identity
                      (like your name or contact information) but may include
                      device and usage information, such as your IP address,
                      browser and device characteristics, operating system,
                      language preferences, referring URLs, device name,
                      country, location, information about how and when you use
                      our Services, and other technical information.
                    </p>
                  </div>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Section 2 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    2. How Do We Process Your Information?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We process your information to provide, improve,
                    and administer our Services, communicate with you, for
                    security and fraud prevention, and to comply with law. We
                    may also process your information for other purposes with
                    your consent.
                  </p>
                  <p>
                    We process your personal information for a variety of
                    reasons, depending on how you interact with our Services,
                    including:
                  </p>
                  <ul className="grid gap-2 list-disc pl-4">
                    <li>
                      To facilitate account creation and authentication and
                      otherwise manage user accounts
                    </li>
                    <li>
                      To deliver and facilitate delivery of services to the user
                    </li>
                    <li>To respond to user inquiries/offer support to users</li>
                    <li>To send administrative information to you</li>
                    <li>To fulfill and manage your orders</li>
                    <li>To save or protect an individual's vital interest</li>
                  </ul>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    3. What Legal Bases Do We Rely On To Process Your Information?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We only process your personal information when we
                    believe it is necessary and we have a valid legal reason
                    (i.e., legal basis) to do so under applicable law, like with
                    your consent, to comply with laws, to provide you with
                    services to enter into or fulfill our contractual
                    obligations, to protect your rights, or to fulfill our
                    legitimate business interests.
                  </p>
                  <p>
                    If you are located in the EU or UK, this section applies to
                    you. The General Data Protection Regulation (GDPR) and UK
                    GDPR require us to explain the valid legal bases we rely on
                    in order to process your personal information. As such, we
                    may rely on the following legal bases to process your
                    personal information:
                  </p>
                  <ul className="grid gap-2 list-disc pl-4">
                    <li>
                      <strong>Consent.</strong> We may process your information
                      if you have given us permission (i.e., consent) to use
                      your personal information for a specific purpose.
                    </li>
                    <li>
                      <strong>Performance of a Contract.</strong> We may process
                      your personal information when we believe it is necessary
                      to fulfill our contractual obligations to you.
                    </li>
                    <li>
                      <strong>Legal Obligations.</strong> We may process your
                      information where we believe it is necessary for
                      compliance with our legal obligations.
                    </li>
                    <li>
                      <strong>Vital Interests.</strong> We may process your
                      information where we believe it is necessary to protect
                      your vital interests or the vital interests of a third
                      party.
                    </li>
                  </ul>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Section 4 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Globe className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    4. When and With Whom Do We Share Your Personal Information?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We may share information in specific situations
                    described in this section and/or with the following third
                    parties.
                  </p>
                  <p>
                    We may need to share your personal information in the
                    following situations:
                  </p>
                  <ul className="grid gap-2 list-disc pl-4">
                    <li>
                      <strong>Business Transfers.</strong> We may share or
                      transfer your information in connection with, or during
                      negotiations of, any merger, sale of company assets,
                      financing, or acquisition of all or a portion of our
                      business to another company.
                    </li>
                    <li>
                      <strong>Business Partners.</strong> We may share your
                      information with our business partners to offer you
                      certain products, services, or promotions.
                    </li>
                  </ul>
                </div>
              </section>

              {/* Section 5 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                    <Database className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    5. Do We Use Cookies and Other Tracking Technologies?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We may use cookies and other tracking technologies
                    to collect and store your information.
                  </p> 
                  <p>
                    We may use cookies
                    and similar tracking technologies (like web beacons and
                    pixels) to access or store information. Specific information
                    about how we use such technologies and how you can refuse
                    certain cookies is set out in our Cookie Notice.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    6. How Do We Handle Your Social Logins?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> If you choose to register or log in to our
                    services using a social media account, we may have access to
                    certain information about you.
                  </p>
                  <p> 
                    Our Services offer you the
                    ability to register and log in using your third-party social
                    media account details (like your Facebook or Twitter
                    logins). Where you choose to do this, we will receive
                    certain profile information about you from your social media
                    provider.
                  </p>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Section 7 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Globe className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    7. Is Your Information Transferred Internationally?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We may transfer, store, and process your
                    information in countries other than your own.
                  </p>
                  <p>
                    Our servers
                    are located in the United States. If you are accessing our
                    Services from outside the United States, please be aware
                    that your information may be transferred to, stored, and
                    processed by us in our facilities and by those third parties
                    with whom we may share your personal information.
                  </p>
                </div>
              </section>

              {/* Section 8 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    8. How Long Do We Keep Your Information?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We keep your information for as long as necessary
                    to fulfill the purposes outlined in this privacy notice
                    unless otherwise required by law.
                  </p>
                  <p>
                    We will only keep your
                    personal information for as long as it is necessary for the
                    purposes set out in this privacy notice, unless a longer
                    retention period is required or permitted by law.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    9. How Do We Keep Your Information Safe?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> We aim to protect your personal information
                    through a system of organizational and technical security
                    measures.
                  </p>
                  <p>
                    We have implemented appropriate and reasonable
                    technical and organizational security measures designed to
                    protect the security of any personal information we process.
                    However, despite our safeguards and efforts to secure your
                    information, no electronic transmission over the Internet or
                    information storage technology can be guaranteed to be 100%
                    secure.
                  </p>
                </div>
              </section>

              <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Section 10 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    10. What Are Your Privacy Rights?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> In some regions, such as the European Economic
                    Area (EEA), United Kingdom (UK), and Canada, you have rights
                    that allow you greater access to and control over your
                    personal information. You may review, change, or terminate
                    your account at any time.
                  </p>
                  <p>
                    In some regions (like the EEA, UK, and Canada), you have
                    certain rights under applicable data protection laws. These
                    may include the right (i) to request access and obtain a
                    copy of your personal information, (ii) to request
                    rectification or erasure; (iii) to restrict the processing
                    of your personal information; and (iv) if applicable, to
                    data portability.
                  </p>
                </div>
              </section>

              {/* Section 11 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Eye className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    11. Controls for Do-Not-Track Features
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    Most web browsers and some mobile operating systems and
                    mobile applications include a Do-Not-Track ("DNT") feature
                    or setting you can activate to signal your privacy
                    preference not to have data about your online browsing
                    activities monitored and collected. At this stage no uniform
                    technology standard for recognizing and implementing DNT
                    signals has been finalized.
                  </p>
                </div>
              </section>

              {/* Section 12 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    12. Do California Residents Have Specific Privacy Rights?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> Yes, if you are a resident of California, you are
                    granted specific rights regarding access to your personal
                    information.
                  </p>
                  <p> 
                    California Civil Code Section 1798.83, also
                    known as the "Shine The Light" law, permits our users who
                    are California residents to request and obtain from us, once
                    a year and free of charge, information about categories of
                    personal information (if any) we disclosed to third parties
                    for direct marketing purposes.
                  </p>
                </div>
              </section>

              {/* Section 13 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                    <FileText className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    13. Do We Make Updates to This Notice?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    <strong>In Short:</strong> Yes, we will update this notice as necessary to
                    stay compliant with relevant laws.
                  </p>
                  <p>
                    We may update this
                    privacy notice from time to time. The updated version will
                    be indicated by an updated "Revised" date and the updated
                    version will be effective as soon as it is accessible.
                  </p>
                </div>
              </section>

              {/* Section 14 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    14. How Can You Contact Us About This Notice?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    If you have questions or comments about this notice, you may
                    email us at{" "}
                    <a
                      href="mailto:support@zapify.pro"
                      className="text-blue-600 dark:text-blue-400 underline"
                    >
                      support@zapify.pro
                    </a>
                    .
                  </p>
                </div>
              </section>
              
               <Separator className="bg-gray-100 dark:bg-neutral-800" />

              {/* Section 15 */}
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                    15. How Can You Review, Update, or Delete the Data We
                    Collect From You?
                  </h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                  <p>
                    Based on the applicable laws of your country, you may have
                    the right to request access to the personal information we
                    collect from you, change that information, or delete it in
                    some circumstances. To request to review, update, or delete
                    your personal information, please contact us at{" "}
                    <a
                      href="mailto:support@zapify.pro"
                      className="text-blue-600 dark:text-blue-400 underline"
                    >
                      support@zapify.pro
                    </a>
                    .
                  </p>
                </div>
              </section>
            </div>

            <Separator className="bg-gray-100 dark:bg-neutral-800" />

            {/* Contact Us */}
            <section className="text-center bg-gray-50 dark:bg-neutral-800/50 rounded-2xl p-8 border border-gray-100 dark:border-neutral-800">
                <div className="flex flex-col items-center gap-3 mb-4">
                    <div className="p-3 bg-white dark:bg-neutral-800 rounded-full shadow-sm">
                        <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.legal.haveQuestions}</h3>
                </div>
              <p className="text-gray-600 dark:text-gray-400">
                {t.legal.contactEmail}{' '}
                <a
                  href="mailto:support@zapify.pro"
                  className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                >
                  support@zapify.pro
                </a>
                .
              </p>
            </section>

            {/* Back to top */}
            <div className="text-center pt-8">
              <Link to="/">
                <Button size="lg" variant="gradient-brand">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.legal.returnToWebsite}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
